import { Component, EventEmitter, inject, Input, OnInit, Optional, Output } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Address } from "@models/address/address";
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@shared/controls/button/button.component';
import { IndividualData } from "@models/individuals/individual-data";
import { Relationship, ShareholderRelationshipDetails } from "@models/relationships/relationship";
import { CorporateHolderModel, IndividualHolderModel } from '@models/shares/security-registry-record';
import {
  SecurityHolderIconLabelComponent
} from '@shared/components/shares/security-holder-icon-label/security-holder-icon-label.component';
import {
  AddCorporateSecurityHolderModalComponent
} from '../../reusable-form-parts/add-corporate-security-holder-modal/add-corporate-security-holder-modal.component';
import {
  AddIndividualSecurityHolderModalComponent
} from '../../reusable-form-parts/add-individual-security-holder-modal/add-individual-security-holder-modal.component';

@Component({
  selector: 'app-share-joint-security-holder',
  standalone: true,
  imports: [
    SecurityHolderIconLabelComponent,
    ButtonComponent
  ],
  templateUrl: './share-joint-security-holder.component.html',
  styleUrl: './share-joint-security-holder.component.scss'
})
export class ShareJointSecurityHolderComponent implements OnInit, ControlValueAccessor {
  private modalService = inject(NgbModal);

  @Input() label = 'Shareholders'
  @Input() securityHolders: (IndividualHolderModel | CorporateHolderModel)[] = [];
  @Input() suggestedAddresses: Address[] = [];
  @Input() fullNameSuggestions: string[] = [];

  @Output() addSecurityHolder = new EventEmitter<IndividualHolderModel | CorporateHolderModel>();
  @Output() editHolder = new EventEmitter<IndividualHolderModel | CorporateHolderModel>();
  @Output() deleteHolder = new EventEmitter<IndividualHolderModel | CorporateHolderModel>();

  disabled = false;
  #value: (IndividualHolderModel | CorporateHolderModel)[] | null = null;

  constructor(@Optional() protected ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.value = this.value ?? this.securityHolders;
  }

  onEditHolder(holder: IndividualHolderModel | CorporateHolderModel): void {
    if (holder.$type === IndividualHolderModel.$type) {
      this.addIndividual((holder as IndividualHolderModel));
    } else if (holder.$type === CorporateHolderModel.$type) {
      this.addCorporate((holder as CorporateHolderModel));
    }
  }

  onDeleteHolder(holder: IndividualHolderModel | CorporateHolderModel): void {
    const index = this.value.findIndex(h => h === holder);
    this.value = this.value.filter((_, i) => i !== index);
    this.deleteHolder.emit(holder);
  }

  addIndividual(individualHolderModel: IndividualHolderModel | null = null): NgbModalRef {
    const modalRef = this.openModal(AddIndividualSecurityHolderModalComponent);
    modalRef.componentInstance.individualHolderModel = individualHolderModel;
    modalRef.componentInstance.suggestedAddresses = this.suggestedAddresses;
    modalRef.componentInstance.fullNameSuggestions = this.fullNameSuggestions;
    modalRef.result.then(
      (newIndividualSecurityHolder: IndividualHolderModel) => {
        this.addOrReplaceHolder(newIndividualSecurityHolder);
      },
      () => {
      }
    );

    return modalRef;
  }

  addCorporate(corporateHolderModel: CorporateHolderModel | null = null): NgbModalRef {
    const modalRef = this.openModal(AddCorporateSecurityHolderModalComponent);
    modalRef.componentInstance.corporateHolderModel = corporateHolderModel;
    modalRef.componentInstance.suggestedAddresses = this.suggestedAddresses;
    modalRef.result.then(
      (newCorporateSecurityHolder: CorporateHolderModel) => {
        this.addOrReplaceHolder(newCorporateSecurityHolder);
      },
      () => {
      }
    );

    return modalRef;
  }

  addOrReplaceHolder(newHolder: IndividualHolderModel | CorporateHolderModel): void {
    const existingCorporateSecurityHolderIndex = this.value.findIndex(holder => holder?.relationshipId && newHolder?.relationshipId === holder?.relationshipId);

    if (existingCorporateSecurityHolderIndex >= 0) {
      this.value[existingCorporateSecurityHolderIndex] = Object.assign(this.value[existingCorporateSecurityHolderIndex], newHolder);
    } else {
      this.value.push(newHolder);
    }

    this.ngControl.control?.updateValueAndValidity();
  }

  private openModal<T>(component: new () => T): NgbModalRef {
    return this.modalService.open(component, {
      centered: true,
      size: 'sm',
      modalDialogClass: 'modal-sm-plus'
    });
  }

  get value(): (IndividualHolderModel | CorporateHolderModel)[] {
    return this.#value ?? [];
  }

  set value(value: (IndividualHolderModel | CorporateHolderModel)[] | null) {
    this.#value = value ?? [];
    this.onChange(this.#value);
  }

  get displayValidationError(): boolean {
    return !!(this.ngControl && this.ngControl.touched && this.ngControl.errors);
  }

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };

  writeValue(value: (IndividualHolderModel | CorporateHolderModel)[] | null): void {
    this.value = value;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  static toRelationships(holders: (IndividualHolderModel | CorporateHolderModel)[]): Relationship[] {
    return holders.map((holderModel) => {
      if (holderModel.$type === 'CorporateHolderModel') {
        const shareholderRelationshipDetails = new ShareholderRelationshipDetails({ authorisedSignatories: holderModel.details.authorisedSignatories ?? [] });
        holderModel = holderModel as CorporateHolderModel;
        return new Relationship({
          entityDataOverride: holderModel.entityData,
          details: shareholderRelationshipDetails,
          relationshipId: holderModel.relationshipId || undefined,
        });
      } else {
        holderModel = holderModel as IndividualHolderModel;
        return new Relationship({
          individualDataOverride: new IndividualData(holderModel.individualData),
          details: new ShareholderRelationshipDetails()
        });
      }
    });
  }
}
